import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Typography,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useEffect, useState } from "react";
import type { OhaioAgent } from "../../lib/ohaio";

interface Props {
  organizationId: string;
}

// The ohaio agents of the organization, fetched by the backend which knows
// which agents the organization has (hard coded for now, see
// docs/ohaio_agents.md)
export const Agents: React.FC<Props> = ({ organizationId }) => {
  const [agents, setAgents] = useState<OhaioAgent[] | null>(null);
  const [error, setError] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  useEffect(() => {
    setAgents(null);
    setError(false);
    setSelectedAgentId(null);
    fetch(`/rapi/organizations/${organizationId}/agents`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then((result) => {
        setAgents(result.data);
      })
      .catch((error) => {
        console.error("Error:", error);
        setError(true);
      });
  }, [organizationId]);

  if (error) {
    return (
      <Typography color="error">
        <FormattedMessage
          id="agents.error"
          defaultMessage="Unable to load the agents."
        />
      </Typography>
    );
  }

  if (agents === null) {
    return <CircularProgress />;
  }

  const selectedAgent = agents.find((agent) => agent.id === selectedAgentId);
  if (selectedAgent) {
    return (
      <AgentDetail
        agent={selectedAgent}
        onBack={() => setSelectedAgentId(null)}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: "800px",
      }}
    >
      <Typography>
        <FormattedMessage
          id="agents.sentence1"
          defaultMessage="The agents improve the transcription and the translation of your meetings with the vocabulary of your organization: proper names, acronyms and business context."
        />
      </Typography>
      <table className="table table-bordered table-hover">
        <thead>
          <tr>
            <th>
              <FormattedMessage id="apikeys.col-name" defaultMessage="Name" />
            </th>
            <th>
              <FormattedMessage
                id="agents.col-names"
                defaultMessage="Names and acronyms"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {agents.map((agent) => (
            <tr
              key={agent.id}
              onClick={() => setSelectedAgentId(agent.id)}
              style={{ cursor: "pointer" }}
            >
              <td>
                <Link component="button" color="secondary">
                  {agent.name}
                </Link>
                {agent.default && (
                  <Chip
                    size="small"
                    color="secondary"
                    sx={{ ml: 1 }}
                    label={
                      <FormattedMessage
                        id="agents.default"
                        defaultMessage="Default"
                      />
                    }
                  />
                )}
              </td>
              <td>{agent.names.length}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

interface AgentDetailProps {
  agent: OhaioAgent;
  onBack: () => void;
}

// The business context and the corrections of the agent are the ohaio secret
// sauce, the backend doesn't send them
const AgentDetail: React.FC<AgentDetailProps> = ({ agent, onBack }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
        maxWidth: "800px",
      }}
    >
      <Button onClick={onBack} variant="outlined" color="secondary">
        <FormattedMessage
          id="agents.back"
          defaultMessage="Back to the agents"
        />
      </Button>
      <Typography variant="h6">{agent.name}</Typography>
      {agent.default && (
        <Alert severity="info">
          <FormattedMessage
            id="agents.default-sentence"
            defaultMessage="This agent is used by default in all the meetings of this organization."
          />
        </Alert>
      )}
      <Box>
        <Typography sx={{ fontWeight: 600 }}>
          <FormattedMessage
            id="agents.col-names"
            defaultMessage="Names and acronyms"
          />
        </Typography>
        <Typography>{agent.names.join(", ")}</Typography>
      </Box>
    </Box>
  );
};
